import axios, { type InternalAxiosRequestConfig } from 'axios'
import { msalInstance, apiScopes } from '@/features/auth/lib/msalConfig'
import { InteractionRequiredAuthError, type AccountInfo } from '@azure/msal-browser'
import { AUTHZ_KEY, queryClient } from '@/shared/api/queryClient'

/**
 * Axios instance pre-configured for the IR API.
 *
 * Auth strategy: all requests use OAuth2 access tokens (MSAL).
 * The older Azure Functions endpoints used a `code` query-param (function key).
 * That pattern is NOT used here — the back-end must be updated to accept
 * OAuth2 access tokens for any endpoint that previously required ?code=...
 */
export const apiClient = axios.create({
  baseURL: import.meta.env.VITE_API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
})

/**
 * In-flight token acquisition, shared across concurrent requests.
 * Without this, N parallel requests on an expired token each trigger their own
 * interactive prompt.
 */
let tokenRequest: Promise<string> | null = null

async function acquireToken(account: AccountInfo): Promise<string> {
  if (tokenRequest) return tokenRequest

  tokenRequest = (async () => {
    try {
      const result = await msalInstance.acquireTokenSilent({ scopes: apiScopes, account })
      return result.accessToken
    } catch (err) {
      if (err instanceof InteractionRequiredAuthError) {
        // Redirect, not popup: an interceptor runs outside a user gesture, so
        // browsers block popups here and the request would fail or hang.
        await msalInstance.acquireTokenRedirect({ scopes: apiScopes, account })
        // Navigation is underway — never resolve, so nothing is sent unauthenticated.
        return new Promise<string>(() => {})
      }
      throw err
    } finally {
      tokenRequest = null
    }
  })()

  return tokenRequest
}

/** Attach a fresh access token to every outgoing request. */
apiClient.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
  const accounts = msalInstance.getAllAccounts()
  if (accounts.length === 0) {
    // Not signed in — let the request through; the 401 will trigger the auth flow
    return config
  }

  const account = msalInstance.getActiveAccount() ?? accounts[0]
  const token = await acquireToken(account)
  config.headers.Authorization = `Bearer ${token}`

  return config
})

/** Error carrying the HTTP status so callers can branch on it (e.g. 404 feature detection). */
export class ApiError extends Error {
  readonly status?: number

  constructor(message: string, status?: number) {
    super(message)
    this.name = 'ApiError'
    this.status = status
  }
}

/** The error envelope the IR API returns on failure. Both fields are optional. */
interface ApiErrorBody {
  message?: string
  error?: string
}

/** Pull the most specific human-readable detail available out of an error body. */
function extractDetail(data: unknown, fallback: string): string {
  if (typeof data === 'string' && data.trim().length > 0) return data.trim()

  if (data && typeof data === 'object') {
    const body = data as ApiErrorBody
    return body.message ?? body.error ?? JSON.stringify(data)
  }

  return fallback
}

/** Normalise error shape so callers can always read `error.message`. */
apiClient.interceptors.response.use(
  (res) => res,
  (err: unknown) => {
    if (axios.isAxiosError(err)) {
      const status = err.response?.status

      // The API is the authority on access. A rejection means our cached
      // authorization state is stale, so resync immediately rather than
      // waiting for the next poll tick.
      if (status === 401 || status === 403) {
        queryClient.invalidateQueries({ queryKey: AUTHZ_KEY })
      }

      const detail = extractDetail(
        err.response?.data,
        err.message ?? 'An unexpected error occurred',
      )
      const message = status ? `[${status}] ${detail}` : detail

      return Promise.reject(new ApiError(message, status))
    }
    return Promise.reject(err)
  },
)
